import { useMediaQuery } from '@mui/material';
import {
  CompositeFilterDescriptor,
  DataResult,
  DataSourceRequestState
} from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridColumn as Column,
  GridExpandChangeEvent,
  GridSortChangeEvent,
  GridToolbar
} from '@progress/kendo-react-grid';
import { orderBy } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { breakpoints } from 'src/assets/_responsive-mixin';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { useColumns } from 'src/components/shared/DataGrid/ColumnMenu';
import { ComboBoxFilterCell } from 'src/components/shared/DataGrid/ComboBoxFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import {
  DateCell,
  DateTimeCell
} from 'src/components/shared/DataGrid/DateCell';
import { DateRangeFilterCell } from 'src/components/shared/DataGrid/DateRangeFilterCell';
import { StatusCircle } from 'src/components/shared/StatusCircle/StatusCircle';
import { selectGrades } from 'src/store/grades/grades.selectors';
import {
  MixtureDetailDto,
  MixtureDto,
  MixtureProductDto,
  MixtureStatus
} from 'src/store/mixtures/mixture';
import { MixtureApi } from 'src/store/mixtures/mixtures.api';
import { selectMixtureStatuses } from 'src/store/mixtures/mixtures.selectors';
import { selectRunSystems } from 'src/store/runsystems/runsystems.selectors';
import {
  MixturesListColumnMenu,
  getMixtureColumns
} from './MixturesListColumnMenu';
import { MixturesListMobile } from './MixturesListMobile';

type MixturesListProps = {
  data: DataResult;
  loading: boolean;
  requestState: DataSourceRequestState;
  onEdit: (mixture: MixtureDto) => void;
  onDelete: (mixture: MixtureDto) => void;
  onSortChange: (event: GridSortChangeEvent) => void;
  onFilterChange: (filter: CompositeFilterDescriptor | undefined) => void;
  onResetFilters: () => void;
  onPageChange: (skip: number, take: number) => void;
  initExporter: (exporter: ExcelExport) => void;
};

export const MixturesList = (props: MixturesListProps) => {
  const {
    data,
    loading,
    requestState,
    onEdit,
    onDelete,
    onSortChange,
    onFilterChange,
    onResetFilters,
    onPageChange,
    initExporter
  } = props;

  const { t } = useTranslation();

  const largeScreen = useMediaQuery(`(min-width: ${breakpoints.large})`);

  const [currentMixture, setCurrentMixture] =
    useState<Nullable<MixtureDetailDto>>(null);

  const [mixtures, setMixtures] = useState<DataResult>({
    data: [],
    total: 0
  } as DataResult);

  const { columns, isColumnVisible, onColumnsChange } = useColumns<MixtureDto>(
    getMixtureColumns()
  );

  useEffect(() => {
    setMixtures(data);
  }, [data]);

  useEffect(() => {
    setMixtures({
      ...mixtures,
      data: mixtures.data.map(mixture =>
        mixture.id === currentMixture?.id
          ? {
              ...mixture,
              expanded: true,
              mixtureProducts: orderBy(
                currentMixture?.mixtureProducts ?? [],
                mixtureProduct => mixtureProduct.runNo,
                'desc'
              )
            }
          : mixture
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMixture]);

  const mixtureStatuses$ = useSelector(selectMixtureStatuses);
  const runSystems$ = useSelector(selectRunSystems);
  const grades$ = useSelector(selectGrades);

  const onMixtureDetailClick = async (event: GridExpandChangeEvent) => {
    if (event.dataItem.expanded) {
      setMixtures({
        ...mixtures,
        data: mixtures.data.map(mixture =>
          mixture.id === event.dataItem.id
            ? {
                ...mixture,
                expanded: false
              }
            : mixture
        )
      });
    } else {
      const response = await MixtureApi.getById(event.dataItem.id);
      if (response.ok) {
        setCurrentMixture(response.data);
      }
    }
  };

  return (
    <Fragment>
      {largeScreen && (
        <ExcelExport
          data={data.data}
          ref={initExporter}
          columns={[
            ...(isColumnVisible(columns, 'no')
              ? [{ field: 'no', title: 'Nr' }]
              : []),
            ...(isColumnVisible(columns, 'mixDate')
              ? [{ field: 'mixDate', title: 'Blandedato' }]
              : []),
            ...(isColumnVisible(columns, 'mixtureStatusName')
              ? [{ field: 'mixtureStatusName', title: 'Status' }]
              : []),
            ...(isColumnVisible(columns, 'runNo')
              ? [{ field: 'runNo', title: 'Run nr' }]
              : []),
            ...(isColumnVisible(columns, 'lotNo')
              ? [{ field: 'lotNo', title: 'Lot nr' }]
              : []),
            ...(isColumnVisible(columns, 'runSystemName')
              ? [{ field: 'runSystemName', title: 'Runsystem' }]
              : []),
            ...(isColumnVisible(columns, 'totalAmount')
              ? [{ field: 'totalAmount', title: 'Totalt antall' }]
              : []),
            ...(isColumnVisible(columns, 'currentAmount')
              ? [{ field: 'currentAmount', title: 'Antall' }]
              : []),
            ...(isColumnVisible(columns, 'gradeName')
              ? [{ field: 'gradeName', title: 'Grad' }]
              : []),
            ...(isColumnVisible(columns, 'specificationNo')
              ? [{ field: 'specificationNo', title: 'PSP nr' }]
              : []),
            ...(isColumnVisible(columns, 'specificationName')
              ? [{ field: 'specificationName', title: 'PSP' }]
              : [])
          ]}
        >
          <DataGrid
            data={mixtures}
            loading={loading}
            pageable
            pageSize={20}
            sort={requestState.sort}
            skip={requestState.skip}
            take={requestState.take}
            filter={requestState.filter}
            onSortChange={onSortChange}
            onFilterChange={event => {
              onFilterChange(event.filter);
            }}
            onPageChange={onPageChange}
            onRowClick={({ dataItem }) => onEdit(dataItem)}
            expandField="expanded"
            detail={props => (
              <MixtureProducts products={props.dataItem.mixtureProducts} />
            )}
            onExpandChange={onMixtureDetailClick}
          >
            <GridToolbar>
              <MixturesListColumnMenu
                selectedColumns={columns}
                onSelectedColumnsChange={onColumnsChange}
              />
              <SecondaryButton
                label="Nullstill filter"
                name="resetFilters"
                className="ml-8"
                onClick={onResetFilters}
                disabled={
                  !requestState.filter ||
                  requestState.filter.filters.length === 0
                }
              />
            </GridToolbar>

            {isColumnVisible(columns, 'no') && (
              <Column
                field="no"
                title={t('props.number')}
                filter="numeric"
                width="160px"
              />
            )}
            {isColumnVisible(columns, 'mixDate') && (
              <Column
                field="mixDate"
                title={t('mixture.mixDate')}
                cell={props => <DateCell {...props} />}
                filterCell={props => (
                  <DateRangeFilterCell
                    {...props}
                    requestState={requestState}
                    onFilterChange={onFilterChange}
                  />
                )}
                width="210px"
              />
            )}
            {isColumnVisible(columns, 'mixtureStatusName') && (
              <Column
                field="mixtureStatusName"
                title={t('props.status')}
                filterCell={props => (
                  <ComboBoxFilterCell
                    {...props}
                    options={mixtureStatuses$}
                    optionField="name"
                  />
                )}
                cell={({ dataItem }) => (
                  <td>
                    <StatusCircle
                      color={
                        dataItem.mixtureStatusId === MixtureStatus.Finished
                          ? 'var(--green-status-color-rgb)'
                          : 'var(--yellow-status-color-rgb)'
                      }
                      className="mr-16"
                    />

                    {dataItem.mixtureStatusName}
                  </td>
                )}
                width="140px"
              />
            )}
            {isColumnVisible(columns, 'runNo') && (
              <Column
                field="runNo"
                title={t('product.runNo')}
                width="160px"
                filter="numeric"
              />
            )}
            {isColumnVisible(columns, 'lotNo') && (
              <Column
                field="lotNo"
                title={t('product.lotNo')}
                width="160px"
                filter="numeric"
              />
            )}
            {isColumnVisible(columns, 'runSystemName') && (
              <Column
                field="runSystemName"
                title={t('runSystem.singular')}
                filterCell={props => (
                  <ComboBoxFilterCell
                    {...props}
                    options={runSystems$}
                    optionField="name"
                  />
                )}
                width="160px"
              />
            )}
            {isColumnVisible(columns, 'totalAmount') && (
              <Column
                field="totalAmount"
                title={t('props.totalAmount')}
                filterable={false}
                width="120px"
              />
            )}
            {isColumnVisible(columns, 'currentAmount') && (
              <Column
                field="currentAmount"
                title={t('props.amount')}
                filterable={false}
                width="120px"
              />
            )}
            {isColumnVisible(columns, 'totalNumber') && (
              <Column
                field="totalNumber"
                title={t('props.totalCount')}
                filterable={false}
                width="120px"
              />
            )}
            {isColumnVisible(columns, 'currentNumber') && (
              <Column
                field="currentNumber"
                title={t('props.count')}
                filterable={false}
                width="120px"
              />
            )}
            {isColumnVisible(columns, 'gradeName') && (
              <Column
                field="gradeName"
                title={t('grade.singular')}
                filterCell={props => (
                  <ComboBoxFilterCell
                    {...props}
                    options={grades$}
                    optionField="name"
                  />
                )}
                width="200px"
              />
            )}
            {isColumnVisible(columns, 'specificationNo') && (
              <Column
                field="specificationNo"
                title={t('specification.pspNo')}
                filterable={false}
                width="200px"
              />
            )}
            {isColumnVisible(columns, 'specificationName') && (
              <Column
                field="specificationName"
                title={t('specification.psp')}
                filterable={false}
                width="240px"
              />
            )}
            <Column
              width="100px"
              filterable={false}
              cell={props => (
                <td>
                  <DeleteButton onClick={() => onDelete(props.dataItem)} />
                </td>
              )}
            />
          </DataGrid>
        </ExcelExport>
      )}

      {!largeScreen && <MixturesListMobile {...props} />}
    </Fragment>
  );
};

const MixtureProducts: React.FC<{ products: MixtureProductDto[] }> = ({
  products
}) => {
  const { t } = useTranslation();

  return (
    <DataGrid items={products} style={{ marginTop: 20 }}>
      <Column field="runNo" title={t('product.runNo')} />
      <Column field="runSystemName" title={t('runSystem.singular')} />
      <Column field="amount" title={t('props.amount')} format="{0:0.000}" />
      <Column field="gradeName" title={t('grade.singular')} />
      <Column field="lotNo" title={t('product.lotNo')} />
      <Column field="userInitials" title={t('user.initials')} />
      <Column
        field="mixDate"
        title={t('props.date')}
        cell={props => <DateTimeCell {...props} />}
      />
    </DataGrid>
  );
};
